import { Component, OnInit, OnDestroy } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { SnackbarService } from "../../services/snackbar.service"
import { MatDialog, MatDialogModule } from "@angular/material/dialog"
import { MatTooltipModule } from "@angular/material/tooltip"
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator"
import { Subscription } from "rxjs"

import {
  StationRatesService,
  StationRate,
  StationRatesResponse,
} from "../../services/station-rates.service"
import { Station } from "../../models/station.model"
import { AddStationRateDialogComponent } from "./dialogs/add-station-rate-dialog.component"
import { EditStationRateDialogComponent } from "./dialogs/edit-station-rate-dialog.component"
import { BulkUploadDialogComponent } from "./dialogs/bulk-upload-dialog.component"
import { StationHeaderComponent } from "../../shared/station-header/station-header.component"
import {
  CommonTableComponent,
  TableColumn,
  TableAction,
} from "../../shared/common-table/common-table.component"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { PermissionService } from "../../services/permission.service"
import { StationStoreService } from "../../services/station-store.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { canManageStationConfig, normalizeStationRole } from "../../utils/station-role.util"

@Component({
  selector: "app-manage-station-rates",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatTooltipModule,
    MatPaginatorModule,
    StationHeaderComponent,
    CommonTableComponent,
    PageHeaderComponent,
  ],
  templateUrl: "./manage-station-rates.component.html",
  styleUrls: ["./manage-station-rates.component.css"],
})
export class ManageStationRatesComponent implements OnInit, OnDestroy {
  stations: Station[] = []
  selectedStation: Station | null = null
  stationRates: StationRate[] = []
  loading = false
  loadingStations = false
  hasRatesData = false
  canEdit = false
  canDelete = false
  canAdd = false
  isViewOnly = false

  selectedRateIds = new Set<number>()
  isBulkSelectMode = false
  isDeletingBulk = false

  total = 0
  limit = 10
  offset = 0
  pageSizeOptions = [5, 10, 25, 50]

  tableColumns: TableColumn[] = [
    { key: "friendly_name", label: "Package Name", class: "th-name" },
    { key: "spot_length", label: "Spot Length", class: "th-spot" },
    { key: "time_range", label: "Time Range", class: "th-time" },
    { key: "days", label: "Days", class: "th-days" },
    { key: "pricing_type", label: "Entered As", class: "th-source" },
    { key: "cpm", label: "CPM", class: "th-cpm" },
    { key: "ratecard", label: "Rate Card", class: "th-rate" },
  ]

  tableActions: TableAction[] = [
    {
      icon: "edit",
      label: "Edit rate",
      class: "btn-icon btn-edit",
      callback: (rate: StationRate) => this.openEditDialog(rate),
      show: () => this.canEdit,
    },
    {
      icon: "delete",
      label: "Delete rate",
      class: "btn-icon btn-delete",
      callback: (rate: StationRate) => this.deleteRate(rate),
      show: () => this.canDelete,
    },
  ]

  private subscriptions: Subscription[] = []

  constructor(
    private stationRatesService: StationRatesService,
    private stationStore: StationStoreService,
    private router: Router,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private permissionService: PermissionService,
    private confirmDialog: ConfirmDialogService,
  ) { }

  ngOnInit(): void {
    const roleSub = this.permissionService.currentRole$.subscribe(() => {
      this.applyPermissionsForSelectedStation()
    })
    this.subscriptions.push(roleSub)
    this.loadStations()
  }

  private applyPermissionsForSelectedStation(station?: Station | null): void {
    const target = station ?? this.selectedStation
    const role = normalizeStationRole(target?.user_role ?? this.permissionService.getCurrentRole())
    const canManage = canManageStationConfig(role)
    this.canEdit = canManage
    this.canDelete = canManage
    this.canAdd = canManage
    this.isViewOnly = !canManage && ["station_user", "user"].includes(role)
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  loadStations(): void {
    this.loadingStations = true
    const stationsSub = this.stationStore.watchStations().subscribe({
      next: (stations) => {
        this.stations = stations
        if (this.stations.length > 0 && !this.selectedStation) {
          this.selectedStation = this.stations[0]
          this.stationRatesService.setSelectedStation(this.selectedStation)
          this.applyPermissionsForSelectedStation(this.selectedStation)
        }
        this.loadingStations = false
        if (this.selectedStation) {
          this.loadStationRates()
        }
      },
      error: (error) => {
        console.error("Error loading stations:", error)
        this.snackbar.error("Error loading stations", 3000)
        this.loadingStations = false
      },
    })
    this.subscriptions.push(stationsSub)
  }

  onStationChange(station: Station): void {
    this.selectedStation = station
    this.stationRatesService.setSelectedStation(this.selectedStation)
    this.applyPermissionsForSelectedStation(station)
    this.isBulkSelectMode = false
    this.selectedRateIds.clear()
    this.loadStationRates()
  }

  loadStationRates(): void {
    if (!this.selectedStation) return

    this.loading = true

    const ratesSub = this.stationRatesService.getStationRates(this.selectedStation.id, this.limit, this.offset).subscribe({
      next: (response: StationRatesResponse | any) => {
        if (Array.isArray(response?.rates)) {
          this.stationRates = response.rates
          this.total = response.pagination?.total ?? response.total ?? response.rates.length
        } else if (Array.isArray(response?.data?.rates)) {
          this.stationRates = response.data.rates
          this.total = response.data.total || response.data.rates.length
        } else if (Array.isArray(response)) {
          this.stationRates = response
          this.total = response.length
        } else {
          this.stationRates = []
          this.total = 0
        }

        this.hasRatesData = this.stationRates.length > 0
        this.loading = false
      },
      error: (error) => {
        console.error("Error loading station rates:", error)
        this.snackbar.error("Error loading station rates", 3000)
        this.stationRates = []
        this.hasRatesData = false
        this.loading = false
      },
    })

    this.subscriptions.push(ratesSub)
  }

  goBack(): void {
    this.router.navigate(["/userhome"])
  }

  refreshData(): void {
    this.loadStationRates()
  }

  onPageChange(event: PageEvent): void {
    this.limit = event.pageSize
    this.offset = event.pageIndex * event.pageSize
    this.loadStationRates()
  }

  openAddDialog(): void {
    if (!this.canAdd) {
      this.snackbar.warning("You don't have permission to add station rates", 3000)
      return
    }
    if (!this.selectedStation) return

    const dialogRef = this.dialog.open(AddStationRateDialogComponent, {
      width: "min(520px, 96vw)",
      maxWidth: "96vw",
      maxHeight: "90vh",
      panelClass: "app-station-rate-dialog",
      autoFocus: false,
      data: { station: this.selectedStation },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadStationRates()
      }
    })
  }

  openEditDialog(rate: StationRate): void {
    if (!this.canEdit) {
      this.snackbar.warning("You don't have permission to edit station rates", 3000)
      return
    }
    const dialogRef = this.dialog.open(EditStationRateDialogComponent, {
      width: "min(520px, 96vw)",
      maxWidth: "96vw",
      maxHeight: "90vh",
      panelClass: "app-station-rate-dialog",
      autoFocus: false,
      data: { rate, station: this.selectedStation },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadStationRates()
      }
    })
  }

  openBulkRatesDialog(): void {
    if (!this.canAdd) {
      this.snackbar.warning("You don't have permission to bulk upload rates", 3000)
      return
    }
    if (!this.selectedStation) return

    const dialogRef = this.dialog.open(BulkUploadDialogComponent, {
      width: "min(900px, 96vw)",
      maxWidth: "96vw",
      maxHeight: "90vh",
      panelClass: "app-station-rate-dialog",
      data: { station: this.selectedStation },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadStationRates()
      }
    })
  }

  deleteRate(rate: StationRate): void {
    if (!this.canDelete) {
      this.snackbar.warning("You don't have permission to delete station rates", 3000)
      return
    }
    this.confirmDialog
      .open({
        title: "Delete station rate",
        message: `Are you sure you want to delete the rate "${rate.friendly_name}"?`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return

        const deleteSub = this.stationRatesService.deleteStationRate(rate.id).subscribe({
          next: () => {
            this.snackbar.success("Station rate deleted successfully", 3000)
            this.loadStationRates()
          },
          error: (error) => {
            console.error("Error deleting station rate:", error)
            this.snackbar.error("Error deleting station rate", 3000)
          },
        })
        this.subscriptions.push(deleteSub)
      })
  }

  toggleBulkSelectMode(): void {
    this.isBulkSelectMode = !this.isBulkSelectMode
    if (!this.isBulkSelectMode) {
      this.selectedRateIds.clear()
    }
  }

  toggleRateSelection(rateId: number): void {
    if (this.selectedRateIds.has(rateId)) {
      this.selectedRateIds.delete(rateId)
    } else {
      this.selectedRateIds.add(rateId)
    }
  }

  selectAllRates(): void {
    this.stationRates.forEach((r) => this.selectedRateIds.add(r.id))
  }

  clearSelection(): void {
    this.selectedRateIds.clear()
  }

  get selectedCount(): number {
    return this.selectedRateIds.size
  }

  confirmBulkDelete(): void {
    if (this.selectedRateIds.size === 0) return

    this.confirmDialog
      .open({
        title: "Delete Selected Rates",
        message:
          `Are you sure you want to delete ${this.selectedRateIds.size} selected rate(s)? ` +
          `Rates with existing orders will be skipped.`,
        confirmLabel: "Delete Selected",
        cancelLabel: "Cancel",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.executeBulkDelete()
      })
  }

  executeBulkDelete(): void {
    this.isDeletingBulk = true
    const rateIds = Array.from(this.selectedRateIds)

    const deleteSub = this.stationRatesService.bulkDeleteRates(rateIds).subscribe({
      next: (res) => {
        this.isDeletingBulk = false
        this.isBulkSelectMode = false
        this.selectedRateIds.clear()

        if (res.skipped_count > 0) {
          this.snackbar.info(
            `${res.deleted_count} rates deleted. ${res.skipped_count} rates skipped (have existing orders).`,
          )
        } else {
          this.snackbar.success(`${res.deleted_count} rates deleted successfully.`)
        }
        this.loadStationRates()
      },
      error: () => {
        this.isDeletingBulk = false
        this.snackbar.error("Failed to delete rates. Please try again.")
      },
    })
    this.subscriptions.push(deleteSub)
  }

  formatTimeRange(startHour: number, endHour: number): string {
    return this.stationRatesService.formatTimeRange(startHour, endHour)
  }

  formatDays(days: StationRate["days"]): string {
    return this.stationRatesService.formatDays(days)
  }

  formatCurrency(amount: number): string {
    return this.stationRatesService.formatCurrency(amount)
  }

  formatPricingType(pricingType?: "cpm" | "ratecard"): string {
    return pricingType === "ratecard" ? "Rate Card" : "CPM"
  }

  formatCPM(cpm: number): string {
    return this.stationRatesService.formatCPM(cpm)
  }
}
